import { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Keyboard } from 'swiper/modules'
import SEO from '@components/SEO'
import BlurHashImage from '@components/BlurHashImage'
import { BLUR_HASH_MAP } from '@constants/BlurHashItems'
import { imageLoadCache } from '@utils/image-load-cache'
import Video from './components/Video'
import Image from './components/Image'
import teaserVideo from '../../assets/images/gallery/video/teaser.mp4'
import teaserThumb from '../../assets/images/gallery/video/thumb/teaser_thumb.webp'
import 'swiper/css'
import 'swiper/css/navigation'
import './GalleryPage.css'

// Load all images inside gallery/images/ dynamically
const imagesGlob = import.meta.glob(
  '../../assets/images/gallery/images/**/*.{jpg,jpeg,png,webp}',
  { eager: true, import: 'default' }
)

const FOLDER_ORDER = {
  'phoi_canh': 1,
  '1PN': 2,
  '2PN': 3,
}

const GALLERY_IMAGES = Object.entries(imagesGlob)
  .map(([path, src], index) => {
    const parts = path.split('/')
    const filename = parts[parts.length - 1]
    const folder = parts[parts.length - 2] // e.g., '1PN', '2PN', 'phoi_canh'

    let alt = 'Hình ảnh dự án Fenica'
    if (folder === 'phoi_canh') {
      const num = filename.replace('phoi_canh_', '').replace(/\.[^/.]+$/, '')
      alt = `Phối cảnh dự án - Phân cảnh ${num}`
    } else if (folder === '1PN') {
      const num = filename.replace('can_ho_1pn_', '').replace(/\.[^/.]+$/, '')
      alt = `Căn hộ mẫu 1PN - Hình ảnh ${num}`
    } else if (folder === '2PN') {
      const num = filename.replace('can_ho_2pn_', '').replace(/\.[^/.]+$/, '')
      alt = `Căn hộ mẫu 2PN - Hình ảnh ${num}`
    }

    return {
      id: `img-${index}`,
      src,
      alt,
      folder,
      filename,
    }
  })
  .sort((a, b) => {
    const orderA = FOLDER_ORDER[a.folder] || 99
    const orderB = FOLDER_ORDER[b.folder] || 99
    if (orderA !== orderB) {
      return orderA - orderB
    }
    return a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: 'base' })
  })

const GALLERY_VIDEOS = [
  {
    id: 'v-teaser',
    src: teaserVideo,
    thumb: teaserThumb,
    alt: 'Teaser giới thiệu dự án Fenica',
    isVideo: true,
  },
]

function CarouselSlide({ item, index, onClose }) {
  const isVideo = item.isVideo || (typeof item.src === 'string' && item.src.endsWith('.mp4'))
  const [loaded, setLoaded] = useState(() => isVideo ? true : imageLoadCache.has(item.src))
  return (
    <div className="gallery-carousel__slide" onClick={onClose}>
      <div
        className="gallery-carousel__media-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        {!isVideo && <BlurHashImage hash={BLUR_HASH_MAP.gallery} fadeOut={loaded} />}
        {isVideo ? (
          <video
            src={item.src}
            controls
            autoPlay
            className="gallery-carousel__media"
            style={{ width: '100%', height: '100%', maxHeight: '80dvh', outline: 'none' }}
          />
        ) : (
          <img
            src={item.src}
            alt={item.alt || `Slide ${index + 1}`}
            className="gallery-carousel__media"
            draggable={false}
            onLoad={() => { imageLoadCache.add(item.src); setLoaded(true) }}
          />
        )}
      </div>
    </div>
  )
}

const ChevronLeftIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

const ChevronRightIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

function GalleryPage() {
  const [activeTab, setActiveTab] = useState('anh') // 'phim' or 'anh'
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const swiperRef = useRef(null)

  const currentItems = activeTab === 'phim' ? GALLERY_VIDEOS : GALLERY_IMAGES
  const isLightboxOpen = lightboxIndex !== null

  const openLightbox = (idx) => {
    setLightboxIndex(idx)
    setActiveSlide(idx)
  }

  const closeLightbox = () => {
    setLightboxIndex(null)
  }

  useEffect(() => {
    if (!isLightboxOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [isLightboxOpen])

  return (
    <main className="gallery-page">
      <SEO
        title="Gallery"
        description="Bộ sưu tập hình ảnh và video đẳng cấp của dự án Fenica."
      />
      <div className="gallery-header">
        {/* Title */}
        <h1 className="gallery-title">GALLERY</h1>

        {/* Tabs Control */}
        <div className="gallery-tabs" role="tablist" aria-label="Bộ sưu tập">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'phim'}
            className={`gallery-tab-btn ${activeTab === 'phim' ? 'gallery-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('phim')}
          >
            Phim
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'anh'}
            className={`gallery-tab-btn ${activeTab === 'anh' ? 'gallery-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('anh')}
          >
            Ảnh
          </button>
        </div>
      </div>

      {/* Pane Content */}
      <div className="gallery-content">
        {activeTab === 'phim' ? (
          <div className="gallery-pane" role="tabpanel" aria-label="Phim & VR Tour">
            <div className="gallery-grid">
              {GALLERY_VIDEOS.map((video, idx) => (
                <Video
                  key={video.id}
                  src={video.thumb}
                  alt={video.alt}
                  onClick={() => openLightbox(idx)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="gallery-pane" role="tabpanel" aria-label="Bộ sưu tập ảnh">
            <div className="gallery-grid">
              {GALLERY_IMAGES.map((img, idx) => (
                <Image
                  key={img.id}
                  src={img.src}
                  alt={img.alt}
                  onClick={() => openLightbox(idx)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {isLightboxOpen && (
        <div
          className="gallery-carousel"
          role="dialog"
          aria-modal="true"
          aria-label="Xem chi tiết"
          onClick={closeLightbox}
        >
          {/* Counter */}
          <div
            className="gallery-carousel__counter"
            onClick={(e) => e.stopPropagation()}
          >
            {activeSlide + 1} / {currentItems.length}
          </div>

          {/* Stage */}
          <div className="gallery-carousel__stage">
            {/* Prev */}
            <button
              type="button"
              className="gallery-carousel__btn gallery-carousel__btn--prev"
              aria-label="Ảnh trước"
              onClick={(e) => {
                e.stopPropagation()
                swiperRef.current?.swiper?.slidePrev()
              }}
            >
              <ChevronLeftIcon />
            </button>

            {/* Next */}
            <button
              type="button"
              className="gallery-carousel__btn gallery-carousel__btn--next"
              aria-label="Ảnh sau"
              onClick={(e) => {
                e.stopPropagation()
                swiperRef.current?.swiper?.slideNext()
              }}
            >
              <ChevronRightIcon />
            </button>

            <Swiper
              ref={swiperRef}
              modules={[Navigation, Keyboard]}
              initialSlide={lightboxIndex}
              slidesPerView={1}
              spaceBetween={30}
              centeredSlides
              keyboard={{ enabled: true }}
              onSlideChange={(s) => setActiveSlide(s.activeIndex)}
              className="gallery-carousel__swiper"
            >
              {currentItems.map((item, idx) => (
                <SwiperSlide key={item.id}>
                  <CarouselSlide item={item} index={idx} onClose={closeLightbox} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Caption */}
          <div
            className="gallery-carousel__caption"
            onClick={(e) => e.stopPropagation()}
          >
            {currentItems[activeSlide]?.alt}
          </div>
        </div>
      )}
    </main>
  )
}

export default GalleryPage
